import React, { useState, useEffect, useRef } from 'react';

// Auto-resizing textarea component
const AutoTextArea = ({ value, onChange, className, placeholder }) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      // Reset height to auto to get the correct scrollHeight
      textareaRef.current.style.height = 'auto';
      // Set the height to the scrollHeight
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [value]);

  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={onChange}
      className={`${className} overflow-hidden resize-none`}
      placeholder={placeholder}
      rows={1}
    />
  );
};

const ProjectVisualEditor = ({ onSave }) => {
  const [data, setData] = useState([]);

  // Load initial data
  useEffect(() => {
    import('./ProjectData').then(module => {
      // Deep clone to avoid mutating the original imported object
      setData(JSON.parse(JSON.stringify(module.ProjectData)));
    }).catch(err => console.error("Failed to load ProjectData", err));
  }, []);

  const handleProjectChange = (index, field, value) => {
    const newData = [...data];
    newData[index][field] = value;
    setData(newData);
  };

  const handleArrayChange = (projectIndex, field, arrayIndex, value) => {
    const newData = [...data];
    newData[projectIndex][field][arrayIndex] = value;
    setData(newData);
  };

  const addArrayItem = (projectIndex, field) => {
    const newData = [...data];
    newData[projectIndex][field].push('');
    setData(newData);
  };

  const removeArrayItem = (projectIndex, field, arrayIndex) => {
    const newData = [...data];
    newData[projectIndex][field].splice(arrayIndex, 1);
    setData(newData);
  };

  const addProject = () => {
    setData([...data, {
      title: '',
      type: '',
      description: '',
      technologies: [],
      features: [],
      github: '',
      images: []
    }]);
  };

  const removeProject = (index) => {
    const newData = [...data];
    newData.splice(index, 1);
    setData(newData);
  };

  const handleSave = () => {
    onSave(JSON.stringify(data, null, 2));
  };

  if (!data.length) return <div className="p-4 text-gray-500">Loading...</div>;

  return (
    <div className="space-y-8 pb-20">
      <div className="flex justify-between items-center sticky top-0 bg-white dark:bg-gray-800 z-10 py-4 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-xl font-bold text-gray-800 dark:text-white">Project Data Editor</h2>
        <button 
          onClick={handleSave}
          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors"
        >
          Apply Changes to JSON
        </button>
      </div>

      {data.map((project, projectIndex) => (
        <div key={projectIndex} className="bg-gray-50 dark:bg-gray-800/50 p-6 rounded-lg border border-gray-200 dark:border-gray-700 relative">
          <button 
            onClick={() => removeProject(projectIndex)}
            className="absolute top-4 right-4 text-red-500 hover:text-red-700 text-sm font-medium"
          >
            Remove Project
          </button>
          
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">
            Project {projectIndex + 1}: {project.title || 'Untitled'}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Title</label>
              <input 
                type="text" 
                className="w-full p-2 border rounded-md dark:bg-gray-900 dark:border-gray-600 dark:text-white" 
                value={project.title}
                onChange={(e) => handleProjectChange(projectIndex, 'title', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Type</label>
              <input 
                type="text" 
                className="w-full p-2 border rounded-md dark:bg-gray-900 dark:border-gray-600 dark:text-white" 
                value={project.type}
                onChange={(e) => handleProjectChange(projectIndex, 'type', e.target.value)}
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Description</label>
            <AutoTextArea 
              className="w-full p-2 border rounded-md dark:bg-gray-900 dark:border-gray-600 dark:text-white" 
              value={project.description}
              onChange={(e) => handleProjectChange(projectIndex, 'description', e.target.value)}
            />
          </div>

          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">GitHub URL</label>
            <input 
              type="text" 
              className="w-full p-2 border rounded-md dark:bg-gray-900 dark:border-gray-600 dark:text-white" 
              value={project.github}
              onChange={(e) => handleProjectChange(projectIndex, 'github', e.target.value)}
            />
          </div>

          {/* Technologies */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Technologies</label>
              <button 
                onClick={() => addArrayItem(projectIndex, 'technologies')}
                className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 px-2 py-1 rounded hover:bg-blue-200 dark:hover:bg-blue-800"
              >
                + Add Tech
              </button>
            </div>
            <div className="space-y-2">
              {project.technologies.map((tech, techIndex) => (
                <div key={techIndex} className="flex gap-2">
                  <input 
                    type="text" 
                    className="flex-1 p-2 border rounded-md dark:bg-gray-900 dark:border-gray-600 dark:text-white" 
                    value={tech}
                    onChange={(e) => handleArrayChange(projectIndex, 'technologies', techIndex, e.target.value)}
                  />
                  <button 
                    onClick={() => removeArrayItem(projectIndex, 'technologies', techIndex)}
                    className="px-3 py-2 bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 rounded-md hover:bg-red-200 dark:hover:bg-red-800/50"
                  >
                    X
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Features */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Features</label>
              <button 
                onClick={() => addArrayItem(projectIndex, 'features')}
                className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 px-2 py-1 rounded hover:bg-blue-200 dark:hover:bg-blue-800"
              >
                + Add Feature
              </button>
            </div>
            <div className="space-y-2">
              {project.features.map((feature, featureIndex) => (
                <div key={featureIndex} className="flex gap-2">
                  <AutoTextArea 
                    className="flex-1 p-2 border rounded-md dark:bg-gray-900 dark:border-gray-600 dark:text-white" 
                    value={feature}
                    onChange={(e) => handleArrayChange(projectIndex, 'features', featureIndex, e.target.value)}
                  />
                  <button 
                    onClick={() => removeArrayItem(projectIndex, 'features', featureIndex)}
                    className="px-3 py-2 bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 rounded-md hover:bg-red-200 dark:hover:bg-red-800/50 h-fit"
                  >
                    X
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Images */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Images</label>
              <button 
                onClick={() => addArrayItem(projectIndex, 'images')}
                className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 px-2 py-1 rounded hover:bg-blue-200 dark:hover:bg-blue-800"
              >
                + Add Image
              </button>
            </div>
            <div className="space-y-2">
              {project.images.map((image, imageIndex) => (
                <div key={imageIndex} className="flex gap-2">
                  <input 
                    type="text" 
                    className="flex-1 p-2 border rounded-md dark:bg-gray-900 dark:border-gray-600 dark:text-white" 
                    value={image}
                    onChange={(e) => handleArrayChange(projectIndex, 'images', imageIndex, e.target.value)}
                  />
                  <button 
                    onClick={() => removeArrayItem(projectIndex, 'images', imageIndex)}
                    className="px-3 py-2 bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 rounded-md hover:bg-red-200 dark:hover:bg-red-800/50"
                  >
                    X
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}

      <button 
        onClick={addProject}
        className="w-full py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors font-medium"
      >
        + Add New Project
      </button>
    </div>
  );
};

export default ProjectVisualEditor;
